// ---------------------------------------------------------------------------
// What time scale a project's 4 input series (precipitation/ETP/temperature/
// pumping) are in — a project-level setting (EvalHidProject.seriesScale),
// not per-series: the engine (engine.ts) reads all 4 series with the SAME
// row index for the same step, so mixing scales within one project would
// silently misalign them. Matches the real desktop engine's own
// Escala_Temporal (Ent_Config.csv, 1=day/2=month — see exportCsvProject.ts),
// including the real behavior that no model parameter is ever auto-rescaled
// between scales (EvalHid_Calculo has that conversion written out, `*30.5`
// for Alfa/degree-day, but commented out/disabled) — a project's own
// parameters have to already be calibrated for whichever scale it uses.
//
// Table-driven, not a scattered daily-vs-monthly if/else, so a future scale
// (hourly, annual...) is one new entry here rather than a hunt through every
// call site: each scale owns its own paste format and date parser — no
// shared "count the fields" heuristic that has to keep learning about every
// scale that gets added (a real design mistake caught before implementing —
// counting `/`-separated fields doesn't generalize to an hourly format that
// needs a completely different separator).
// ---------------------------------------------------------------------------
import type { TextKey } from "../i18n/es.ts";
import { TranslatableError, type Translate } from "../i18n/translate.ts";
import { addDaysIso, addMonthsIso } from "./isoDate.ts";
import { nativeGranularity } from "./aggregates.ts";

export type SeriesScale = "daily" | "monthly";
// Future: add "hourly" | "annual" here and to SERIES_SCALE_INFO/
// SCALE_TRY_ORDER below — nothing else in this file (or its callers) special-
// cases the number/names of scales beyond those two spots.

/**
 * Real incident found live: a CSV whose every line was accidentally wrapped
 * in a stray `"` (e.g. `"01/01/1951;...`, the whole row quoted, not real
 * per-field CSV quoting) used to freeze the tab solid. The plain truthiness
 * check below let `day`/`month`/`year` through with the quote character
 * still attached (e.g. day `'"01'`), producing a garbled-but-non-null ISO
 * string (`Number('"01')` is `NaN`, so `addDaysIso` on it collapses to a
 * FIXED, non-advancing "0NaN-NaN-NaN" — every further `addStep` on THAT
 * string reproduces the exact same string, since NaN arithmetic never
 * changes). `findScaleDateGaps`'s own gap-walk (`while (expected <
 * dates[i]) { ...; expected = addStep(expected) }`) has no other
 * termination check — lexicographically "0NaN-NaN-NaN" sorts before any
 * real "19xx-..."/"20xx-..." date (leading "0" < leading "1"/"2"), so the
 * loop's own condition stayed true forever, endlessly pushing the same
 * string into an ever-growing array. Requiring day/month/year to be pure
 * digit runs rejects this at the actual parse boundary instead — the whole
 * date field fails to parse (a real, immediate, translated error), rather
 * than silently producing something that LOOKS like a date and only
 * detonates several calls later, deep inside unrelated gap-detection math.
 */
function dayMonthYearToIso(day: string, month: string, year: string): string | null {
  if (!/^\d+$/.test(day) || !/^\d+$/.test(month) || !/^\d+$/.test(year)) return null;
  return `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

interface SeriesScaleInfo {
  labelKey: TextKey;
  /** Shown as the paste box's format hint/placeholder — the one canonical format users are told to use for this scale. */
  pasteFormatKey: TextKey;
  /** Full sentence (already names the expected format) for when a pasted/uploaded date doesn't match this scale. */
  dateFormatMismatchKey: TextKey;
  /**
   * Full prose describing every date shape THIS scale's own parser accepts
   * (canonical + tolerated variants, e.g. monthly's own day-fixed-01
   * convention) — the one building block the "project scale not decided
   * yet" hints (PasteDailySeries.tsx/SeriesPanel.tsx) are assembled from at
   * runtime (see `allScalesFormatDescription` below), so a future scale
   * (hourly/annual) shows up there automatically instead of needing two
   * more hand-written sentences edited by hand.
   */
  formatDescriptionKey: TextKey;
  /** One calendar step forward, for gap-detection and date-axis building. */
  addStep(iso: string): string;
  /**
   * Tries to read `dateText` as THIS scale's own date shape — `null` if it
   * doesn't even look like this scale's format (not a "this scale but
   * malformed" error, a plain "not this one", so a caller trying several
   * scales in turn can move on to the next). Always normalizes to an ISO
   * `yyyy-mm-dd` string, monthly always landing on the 1st of the month.
   */
  tryParseDate(dateText: string): string | null;
  /** ISO `yyyy-mm-dd` -> the scale's own display/paste shape (dd/mm/aaaa or mm/aaaa) — the exact inverse of `tryParseDate`'s canonical case, used for chart axes and range labels so a monthly project never shows a day-of-month that doesn't mean anything. */
  formatDate(iso: string): string;
}

const SERIES_SCALE_INFO: Record<SeriesScale, SeriesScaleInfo> = {
  daily: {
    labelKey: "seriesScale.daily",
    pasteFormatKey: "seriesScale.dailyPasteFormat",
    dateFormatMismatchKey: "seriesScale.dailyDateFormatMismatch",
    formatDescriptionKey: "seriesScale.dailyPasteFormat",
    addStep: (iso) => addDaysIso(iso, 1),
    tryParseDate: (dateText) => {
      const fields = dateText.trim().split("/");
      return fields.length === 3 ? dayMonthYearToIso(fields[0], fields[1], fields[2]) : null;
    },
    formatDate: (iso) => {
      const [year, month, day] = iso.split("-");
      return `${day}/${month}/${year}`;
    },
  },
  monthly: {
    labelKey: "seriesScale.monthly",
    pasteFormatKey: "seriesScale.monthlyPasteFormat",
    dateFormatMismatchKey: "seriesScale.monthlyDateFormatMismatch",
    formatDescriptionKey: "seriesScale.monthlyFormatDescription",
    addStep: (iso) => addMonthsIso(iso, 1),
    tryParseDate: (dateText) => {
      const fields = dateText.trim().split("/");
      // "mm/aaaa" (2 fields) is the canonical monthly format (paste always
      // uses it); "dd/mm/aaaa" with the day fixed at "01" (3 fields) is the
      // OTHER real convention a desktop-exported Ent_Precip.csv can use for
      // a monthly project (confirmed against Form1.vb's own
      // InfiereFormatoFechas) — both mean the same thing, neither decides
      // the scale on its own (see the file header).
      if (fields.length === 2) {
        return dayMonthYearToIso("01", fields[0], fields[1]);
      }
      if (fields.length === 3 && fields[0] === "01") {
        return dayMonthYearToIso(fields[0], fields[1], fields[2]);
      }
      return null;
    },
    formatDate: (iso) => {
      const [year, month] = iso.split("-");
      return `${month}/${year}`;
    },
  },
};

export function pasteFormatKey(scale: SeriesScale): TextKey {
  return SERIES_SCALE_INFO[scale].pasteFormatKey;
}

/**
 * "{label}: {format description}" for every known scale, joined into one
 * string — what the "project scale not decided yet" hints
 * (pasteSeries.formatHintUnknownScale, and SeriesPanel.tsx's own CSV
 * upload hint) show instead of a hand-written sentence naming daily and
 * monthly explicitly. Built from SERIES_SCALE_INFO at runtime, so adding
 * a scale here (see the file header) is the ONLY change those hints need
 * — no hardcoded string to remember to update in es.ts/en.ts.
 */
export function allScalesFormatDescription(t: Translate): string {
  return SCALE_TRY_ORDER.map(
    (scale) =>
      `${t(SERIES_SCALE_INFO[scale].labelKey)}: ${t(SERIES_SCALE_INFO[scale].formatDescriptionKey)}`
  ).join("; ");
}

/** ISO `yyyy-mm-dd` -> the scale's own display shape (dd/mm/aaaa or mm/aaaa) — see `SeriesScaleInfo.formatDate`. */
export function formatScaleDate(iso: string, scale: SeriesScale): string {
  return SERIES_SCALE_INFO[scale].formatDate(iso);
}

// Tried in this order when the scale isn't known yet (opening a project from
// a folder/CSV for the very first time — there's nothing to validate
// against). Finest first, so an unambiguous daily date (day != "01") is
// never mistaken for anything else.
const SCALE_TRY_ORDER: SeriesScale[] = ["daily", "monthly"];

/**
 * Parses one date field. With `expectedScale` given, uses ONLY that scale's
 * own format and throws a scale-specific error if it doesn't match — the
 * normal case, once a project already has a declared `seriesScale` (paste,
 * or uploading a further series into a project that already has data).
 * Without it (bootstrapping: opening a project/CSV folder for the first
 * time, before anything has established a scale yet), tries each known
 * scale's own parser in turn and returns whichever recognizes the shape —
 * same principle the real desktop app's own format inference uses.
 */
export function parseScaleDateField(dateText: string, expectedScale?: SeriesScale): string {
  if (expectedScale) {
    const parsed = SERIES_SCALE_INFO[expectedScale].tryParseDate(dateText);
    if (parsed === null) {
      throw new TranslatableError(SERIES_SCALE_INFO[expectedScale].dateFormatMismatchKey, {
        date: dateText,
      });
    }
    return parsed;
  }
  for (const scale of SCALE_TRY_ORDER) {
    const parsed = SERIES_SCALE_INFO[scale].tryParseDate(dateText);
    if (parsed !== null) return parsed;
  }
  throw new TranslatableError("seriesScale.dateFormatUnrecognized", { date: dateText });
}

/**
 * The finest scale `dates` actually supports, from their own spacing —
 * thin wrapper over `aggregates.ts`'s `nativeGranularity` (already tested,
 * already used for exactly this daily-vs-monthly distinction on the results
 * side) so input-series code doesn't need to know it's reusing a
 * results-granularity helper under the hood. Used ONLY as a defensive
 * check/bootstrap signal — never to pick which parser runs (see
 * `parseScaleDateField` above).
 */
export function detectScale(dates: string[]): SeriesScale {
  return nativeGranularity(dates);
}

/**
 * Row index of `targetIso` in a sorted-ascending `dates` array — for
 * SeriesTable.tsx's own date search ("go to date", explicitly NOT a
 * filter, per the user's own request): the exact match if `targetIso` is
 * present, otherwise the first date AFTER it — never before, so searching
 * a date that falls inside a gap lands past the gap rather than on the
 * last good day before it. Clamped to the last index if `targetIso` is
 * later than every date in the series (or if `dates` is empty, to 0 —
 * doesn't happen in practice, SeriesTable.tsx only renders once a column
 * has data, but a plain unguarded lower bound is simpler than asserting
 * non-empty here).
 */
export function findDateRowIndex(dates: string[], targetIso: string): number {
  if (dates.length === 0) return 0;
  let lo = 0;
  let hi = dates.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (dates[mid] < targetIso) lo = mid + 1;
    else hi = mid;
  }
  return Math.min(lo, dates.length - 1);
}

/**
 * Same walk as the old (removed) `dailySeries.ts` `findDateGaps`, but
 * advancing by `scale`'s own step instead of always assuming 1 day —
 * daily behavior is byte-for-byte unchanged from before this file existed.
 *
 * The `next === expected` check is a defensive backstop, not something
 * expected to ever trigger now that `dayMonthYearToIso` rejects a malformed
 * date field at the actual parse boundary (see its own comment for the real
 * incident this guards against — a browser tab frozen solid) — but a value
 * ever reaching this loop that `addStep` can't genuinely advance (any real
 * calendar date always moves forward by at least one step) is exactly the
 * shape of bug that turns into a silent infinite loop, the single worst
 * failure mode this app can have. Cheap enough to keep unconditionally.
 */
export function findScaleDateGaps(dates: string[], scale: SeriesScale): string[] {
  const missing: string[] = [];
  for (let i = 1; i < dates.length; i++) {
    let expected = SERIES_SCALE_INFO[scale].addStep(dates[i - 1]);
    while (expected < dates[i]) {
      missing.push(expected);
      const next = SERIES_SCALE_INFO[scale].addStep(expected);
      if (next === expected) break;
      expected = next;
    }
  }
  return missing;
}
